import { useCallback, useEffect, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { atom, useAtomValue, useSetAtom } from 'jotai'

type SoketId = 'priceFeed'
  | 'gateway'
  | 'bustabit'

const socketsAtom = atom<Record<string, Socket>>({})
const addSocketClientAtom = atom<null, any[], any>(null, (_get, set, { newId, newSocket }) => {
  set(socketsAtom, (prev) => ({ ...prev, [newId]: newSocket }))
})

/**
 * A react hook that uses a given socket ID for connection and disconnection events to a server using a specific url.
 *
 * @param {SoketId} socketId - ID to identify the socket being used for communication.
 * @param {string} serverUrl - Server URL for the socket connection.
 *
 * @return {Object} - Returns an object containing `isConnected` indicating the status of the current socket connection.
 */
export const useSocket = (socketId: SoketId, serverUrl: string): { isConnected: boolean } => {
  const addSocketClient = useSetAtom(addSocketClientAtom)

  const [isConnected, setIsConnected] = useState<boolean>(false)

  useEffect(() => {
    const ioSocket = io(serverUrl, {
      transports: ['websocket'],
    })

    ioSocket.on('connect', () => {
      console.log(`Socket ${socketId} connected`)
      addSocketClient({ newId: socketId, newSocket: ioSocket })
      setIsConnected(true)
    })
    ioSocket.on('disconnect', () => {
      console.log(`Socket ${socketId} disconnected`)
      addSocketClient({ newId: socketId, newSocket: undefined })
      setIsConnected(false)
    })
    ioSocket.io.on('reconnect', (attempt: any) => {
      console.log(`Socket ${socketId} reconnection`, { attempt })
    })
    return () => {
      ioSocket.off('connect')
      ioSocket.off('disconnect')
      ioSocket.disconnect()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { isConnected }
}

/**
 * A react hook that creates a callback to emit messages over a given socket.
 *
 * @param {SoketId} socketId - The ID of the socket over which to emit messages.
 * @return {any} - Returns an object containing `gatewayEmit` method for emitting messages.
 */
export const useSocketEmitter = (socketId: SoketId) => {
  const socket = (useAtomValue(socketsAtom))[socketId]

  /**
   * Emit a message over a specified channel
   *
   * @param {string} channel - The channel over which to emit the message.
   * @param {any} message - The message to emit.
   */
  const emit = useCallback(
    (channel: string, message: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (socket) socket.emit(channel, message)
    },
    [socket],
  )

  return { emit }
}

/**
 * Hook that manages subscriptions to a given topic on a specified socket connection.
 *
 * @param {string} socketId - Identifier of the socket to use for the subscription.
 * @param {string} topic - The topic to which the subscriber must be attached.
 * @param {function} listener - Callback function that processes messages received from the topic.
 */
export const useSocketListener = (socketId: SoketId, topic: string, listener: (msg: any) => void) => {
  const socket = (useAtomValue(socketsAtom))[socketId]

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!socket) return
    socket.on(topic, listener)
    return () => {
      socket.off(topic, listener)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, listener, socket])
}
